import { useRef, useMemo, useState, useEffect, Suspense, Component, ReactNode } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { OrbitControls, Grid } from "@react-three/drei";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader.js";
import { Select, Tag, Space, Checkbox, Button, Row, Col } from "antd";
import * as THREE from "three";

import { useApolloFrame, ApolloFrameApi } from "../../hooks/useApolloFrame";
import { useApolloCommand } from "../../hooks/useApolloCommand";
import HdMap from "./HdMap";
import Obstacles from "./Obstacles";
import Paths from "./Paths";
import TelemetryPanel from "./TelemetryPanel";
import TrafficLights from "./TrafficLights";
import CameraGrid from "./CameraGrid";
import RouteEditor from "./RouteEditor";
import RouteEditPanel from "./RouteEditPanel";
import DriveControlPanel from "./DriveControlPanel";
import { useAuthState } from "../../provider/AuthProvider";
import * as Utils from "../../utils/utils";

// 장애물 타입 색상 범례(Obstacles.tsx / apolloRenderer 색상과 일치)
const OBSTACLE_LEGEND = [
    { c: "#3aa0ff", label: "차량" },
    { c: "#ff5a5a", label: "보행자" },
    { c: "#ffb302", label: "자전거" },
    { c: "#ff2d55", label: "CIPV" },
    { c: "#9aa0a6", label: "가상" },
    { c: "#a07bff", label: "미상" },
];

/**
 * S1 — Dreamview 참조 R3F 3D 뷰 (씬 + 카메라 + 지면 + 자차 placeholder).
 * 좌표계: z-up (Dreamview 컨벤션). DTO x→X, y→Y, height→+Z, heading→rotation.z.
 * 데이터/보간은 useApolloFrame(2D 뷰와 공유 로직)에서 가져온다.
 */

// ── 조명 ──
function Lights() {
    return (
        <>
            <hemisphereLight args={["#aebfd6", "#20262f", 1.0]} />
            <ambientLight intensity={0.5} color={"#8ea0b6"} />
            <directionalLight position={[80, 60, 160]} intensity={1.7} color={"#fff2e0"} />
            <directionalLight position={[-70, -50, 90]} intensity={0.5} color={"#4a5a78"} />
        </>
    );
}

// ── ego 추종 transform (매 프레임 보간 위치로 이동) ──
function useEgoTransform(api: ApolloFrameApi, kind: "obj" | "box") {
    const ref = useRef<any>(null);
    useFrame(() => {
        const e = api.computeEgo(performance.now());
        const g = ref.current;
        if (!g) return;
        if (!e) { g.visible = false; return; }
        g.visible = true;
        g.position.set(e.x, e.y, 0);
        // OBJ: Dreamview 정렬(정적 x=π/2 + heading=y). 박스: z-up에서 heading=z.
        if (kind === "obj") g.rotation.set(Math.PI / 2, e.heading, 0);
        else g.rotation.set(0, 0, e.heading);
    });
    return ref;
}

// ── 자차 3D 모델 (Apollo car.obj + car.mtl, 텍스처 없음) ──
function CarModel({ api }: { api: ApolloFrameApi }) {
    const materials = useLoader(MTLLoader, "/models/car.mtl");
    const obj = useLoader(OBJLoader, "/models/car.obj", (loader) => {
        materials.preload();
        (loader as OBJLoader).setMaterials(materials);
    });
    const car = useMemo(() => obj.clone(), [obj]);
    const ref = useEgoTransform(api, "obj");
    return <primitive ref={ref} object={car} visible={false} />;
}

// ── 박스 자차 (모델 로딩 중/실패 시 fallback) ──
function BoxCar({ api }: { api: ApolloFrameApi }) {
    const ref = useEgoTransform(api, "box");
    return (
        <group ref={ref} visible={false}>
            <mesh position={[0, 0, 0.38]}>
                <boxGeometry args={[4.5, 2.0, 0.75]} />
                <meshStandardMaterial color="#00e5ff" metalness={0.2} roughness={0.5} />
            </mesh>
            <mesh position={[-0.35, 0, 1.05]}>
                <boxGeometry args={[2.2, 1.7, 0.7]} />
                <meshStandardMaterial color="#d8f6ff" metalness={0.1} roughness={0.3} transparent opacity={0.9} />
            </mesh>
        </group>
    );
}

// 모델 로드 실패 시 박스로 대체하는 에러 바운더리
class CarBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { err: boolean }> {
    constructor(p: any) { super(p); this.state = { err: false }; }
    static getDerivedStateFromError() { return { err: true }; }
    render() { return this.state.err ? this.props.fallback : this.props.children; }
}

function EgoCar({ api }: { api: ApolloFrameApi }) {
    return (
        <CarBoundary fallback={<BoxCar api={api} />}>
            <Suspense fallback={<BoxCar api={api} />}>
                <CarModel api={api} />
            </Suspense>
        </CarBoundary>
    );
}

// ── 카메라 추종(top-down): 차량 바로 위에서 내려다보며 이동량만큼 추종.
//    사용자가 팬/줌하면 추종을 멈추고(뷰 고정), '뷰 리셋' 시 차량 위로 복귀·재개 ──
const FOLLOW_HEIGHT = 60; // 초기/리셋 시 top-down 카메라 높이(m). 이후 휠 줌으로 조절
function CameraFollow({ api, controlsRef, resetSeq, topDown }: { api: ApolloFrameApi; controlsRef: React.MutableRefObject<any>; resetSeq: number; topDown: boolean }) {
    const prev = useRef<{ x: number; y: number } | null>(null);
    const inited = useRef(false);
    const lastReset = useRef(resetSeq);
    const userMoved = useRef(false); // 사용자가 팬/줌 시작하면 true → 추종 정지

    // OrbitControls 상호작용 시작 감지 → 추종 정지(뷰 리셋 전까지)
    useEffect(() => {
        const ctr = controlsRef.current;
        if (!ctr) return;
        const onStart = () => { userMoved.current = true; };
        ctr.addEventListener("start", onStart);
        return () => ctr.removeEventListener("start", onStart);
    }, [controlsRef]);

    useFrame(({ camera }) => {
        if (resetSeq !== lastReset.current) { lastReset.current = resetSeq; inited.current = false; } // 뷰 리셋
        const e = api.computeEgo(performance.now());
        if (!e) return;
        const ctr = controlsRef.current;
        if (!inited.current) {
            inited.current = true;
            userMoved.current = false;
            if (topDown) camera.position.set(e.x, e.y, FOLLOW_HEIGHT);   // 차량 바로 위(편집=평면)
            else camera.position.set(e.x - 18, e.y - 18, 16);           // 오블리크(모니터링=약 3D)
            if (ctr) { ctr.target.set(e.x, e.y, 0); ctr.update(); }
            prev.current = { x: e.x, y: e.y };
            return;
        }
        const p = prev.current || { x: e.x, y: e.y };
        const dx = e.x - p.x, dy = e.y - p.y;
        // 사용자가 팬/줌한 뒤엔 추종 정지(뷰 고정). 그 전까지는 이동량만큼 추종.
        if (!userMoved.current && (dx || dy)) {
            camera.position.x += dx; camera.position.y += dy;
            if (ctr) { ctr.target.x += dx; ctr.target.y += dy; ctr.update(); }
        }
        prev.current = { x: e.x, y: e.y };
    });
    return null;
}

/**
 * mode="monitor"(기본): 읽기 전용 모니터링. mode="control": 경로 편집 + 주행 제어 오버레이(R2 별도 페이지).
 * 제어 UI 는 control 모드 + ADMIN/MANAGER 에서만 렌더되며, 라우트도 ADMIN/MANAGER 로 분리된다(설계 §10.5).
 */
export default function Apollo3DView({ mode = "monitor" }: { mode?: "monitor" | "control" }) {
    const api = useApolloFrame();
    const cmd = useApolloCommand();
    const controlsRef = useRef<any>(null);
    const [layers, setLayers] = useState({ map: true, obstacles: true, paths: true, prediction: false });
    const [resetSeq, setResetSeq] = useState(0);
    const control = mode === "control";
    const toggle = (k: keyof typeof layers) => setLayers((s) => ({ ...s, [k]: !s[k] }));

    // 카메라 영상 밴드는 ADMIN 전용
    const userDetails = useAuthState();
    const isAdmin = Utils.roleCheck(userDetails?.user?.authority, [Utils.getAuthority.ROLE_ADMIN]);
    // 경로/주행 제어는 ADMIN/MANAGER 만(설계 §10.5). API 도 동일 권한 강제.
    const isController = Utils.roleCheck(userDetails?.user?.authority, [Utils.getAuthority.ROLE_ADMIN, Utils.getAuthority.ROLE_MANAGER]);
    const showControl = control && isController;
    const startXY: [number, number] | null = api.latest?.ego ? [api.latest.ego.x, api.latest.ego.y] : null;
    const carNumber = api.vehicles.find((v) => v.carId === api.carId)?.carNumber ?? null;
    // 경로 편집 클릭 근접 검증용 도로 폴리라인(차선 중앙선 + 경계선)
    const roadLines = useMemo(() => {
        const m = api.mapData;
        if (!m) return [];
        return [...(m.lanes || []), ...(m.laneBoundaries || [])];
    }, [api.mapData]);
    // 카메라: 제어(편집)=top-down 평면, 모니터링=오블리크(약 3D) — Dreamview 처럼 뷰를 다르게.
    const camPos: [number, number, number] = control ? [0, 0, FOLLOW_HEIGHT] : [-18, -18, 16];

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, height: isAdmin ? "84vh" : "72vh" }}>
        <div style={{ flex: 1, minHeight: 0 }}>
        <Row gutter={[12, 12]} style={{ height: "100%" }}>
            <Col xs={24} lg={18} style={{ height: "100%" }}>
            <div style={{ width: "100%", height: "100%", background: "#000c17", borderRadius: 6, overflow: "hidden", position: "relative" }}>
            {/* 오버레이 UI */}
            <div style={{ position: "absolute", top: 12, left: 12, zIndex: 10 }}>
                <Space>
                    <Select
                        style={{ width: 300 }}
                        placeholder="차량 선택"
                        value={api.carId ?? undefined}
                        onChange={(v) => api.setCarId(v)}
                        options={api.vehicles.map((v) => ({ value: v.carId, label: `${v.carNumber || v.carId} (${v.host})` }))}
                    />
                    <Tag color={api.connected ? "green" : "red"}>{api.connected ? "● 연결됨" : "● 미연결"}</Tag>
                    {api.errorMsg && <Tag color="orange">{api.errorMsg}</Tag>}
                    <Button size="small" onClick={() => setResetSeq((s) => s + 1)} style={{ height: 22, padding: "0 8px", fontSize: 12, lineHeight: "20px" }}>뷰 리셋</Button>
                </Space>
                <div style={{ marginTop: 8, display: "inline-block", background: "rgba(10,16,24,0.66)", border: "1px solid #2a3446", borderRadius: 6, padding: "2px 8px" }}>
                    <Space size={6} wrap>
                        <Checkbox checked={layers.map} onChange={() => toggle("map")}><span style={{ color: "#dbe4f2" }}>맵</span></Checkbox>
                        <Checkbox checked={layers.obstacles} onChange={() => toggle("obstacles")}><span style={{ color: "#dbe4f2" }}>장애물</span></Checkbox>
                        <Checkbox checked={layers.paths} onChange={() => toggle("paths")}><span style={{ color: "#dbe4f2" }}>경로</span></Checkbox>
                        <Checkbox checked={layers.prediction} onChange={() => toggle("prediction")}><span style={{ color: "#dbe4f2" }}>예측</span></Checkbox>
                    </Space>
                </div>
            </div>

            {/* 장애물 색상 범례 (제어 모드에선 하단 주행 제어 패널과 겹쳐 숨김) */}
            {layers.obstacles && !showControl && (
                <div style={{ position: "absolute", bottom: 12, right: 12, zIndex: 10, background: "rgba(10,16,24,0.74)", border: "1px solid #2a3446", borderRadius: 8, padding: "8px 11px", display: "flex", flexDirection: "column", gap: 5 }}>
                    <div style={{ fontSize: 11, color: "#8a99b0", marginBottom: 1 }}>장애물 종류</div>
                    {OBSTACLE_LEGEND.map((o) => (
                        <div key={o.label} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#dbe4f2" }}>
                            <span style={{ width: 12, height: 12, borderRadius: 3, background: o.c, display: "inline-block", flexShrink: 0 }} />
                            {o.label}
                        </div>
                    ))}
                </div>
            )}

            {/* dpr 캡(HiDPI 픽셀 부담 완화) + 고성능 GPU 선호 → 줌 부드럽게 */}
            {/* 편집=top-down 평면(up=+Y), 모니터링=오블리크(up=+Z, 약 3D). 둘 다 회전 없이 팬/줌만. */}
            <Canvas
                camera={{ fov: 40, near: 1, far: 12000, position: camPos }}
                onCreated={({ camera }) => { camera.up.set(0, control ? 1 : 0, control ? 0 : 1); }}
                dpr={[1, 1.5]}
                gl={{ powerPreference: "high-performance", antialias: true }}
            >
                <color attach="background" args={["#000c17"]} />
                <Lights />
                <Grid
                    args={[400, 400]}
                    rotation={[Math.PI / 2, 0, 0]}
                    cellSize={2}
                    cellThickness={0.6}
                    cellColor={"#2a3446"}
                    sectionSize={20}
                    sectionThickness={1}
                    sectionColor={"#3a4a63"}
                    infiniteGrid
                    fadeDistance={2000}
                    fadeStrength={2}
                />
                <HdMap map={api.mapData} visible={layers.map} />
                <TrafficLights signals={api.mapData?.signals} color={api.latest?.signalColor} visible={layers.map} />
                <Paths api={api} showPrediction={layers.prediction} visible={layers.paths} />
                <Obstacles list={api.latest?.obstacles || []} visible={layers.obstacles} />
                {showControl && (
                    <RouteEditor waypoints={cmd.waypoints} addWaypoint={cmd.addWaypoint} startXY={startXY} roadLines={roadLines} />
                )}
                <EgoCar api={api} />
                <CameraFollow api={api} controlsRef={controlsRef} resetSeq={resetSeq} topDown={control} />
                {/* 회전 없음, 우클릭 드래그=지도 이동, 휠=확대/축소. 좌클릭=경로 점 찍기.
                    편집(top-down)은 polar 90°라 maxPolarAngle 미설정, 모니터링(오블리크)은 π/2.05 로 제한. */}
                <OrbitControls
                    ref={controlsRef}
                    makeDefault
                    enableRotate={false}
                    enablePan={true}
                    minDistance={2}
                    maxDistance={4000}
                    maxPolarAngle={control ? undefined : Math.PI / 2.05}
                />
            </Canvas>

            {/* 경로 편집 패널(제어 모드, ADMIN/MANAGER) */}
            {showControl && (
                <RouteEditPanel api={cmd} carId={api.carId} />
            )}
            {/* 주행 제어 패널(제어 모드, ADMIN/MANAGER) */}
            {showControl && (
                <DriveControlPanel
                    carId={api.carId}
                    carNumber={carNumber}
                    engageAdvice={api.latest?.engageAdvice}
                    isAdmin={isAdmin}
                />
            )}
            </div>
            </Col>

            {/* 우측 주행 상태 패널 */}
            <Col xs={24} lg={6} style={{ height: "100%" }}>
                <TelemetryPanel frame={api.latest} />
            </Col>
        </Row>
        </div>

        {/* 하단 카메라 영상 밴드 (ADMIN 전용): 전방 합성 3분할 + 후방 */}
        {isAdmin && (
            <div style={{ height: "22vh", flexShrink: 0, background: "#0a0f18", border: "1px solid #1e2836", borderRadius: 6, padding: 6 }}>
                <CameraGrid carId={api.carId} />
            </div>
        )}
        </div>
    );
}
